// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// constant imports
import { URLConstants } from '../../_constants/url-constants';

// Service import
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// model imports
import { BoardApprovalQueueParameterDto } from '../../shared-acc-board-approval-module/_models/board-approval-queue-parameter-dto';
import { BoardApprovalsActionParametersDto } from '../../shared-acc-board-approval-module/_models/board-approvals-action-parameters-dto';
import { BoardApprovalAllowedActionsParameterDto } from '../_models/board-approval-allowed-actions-parameter-dto';
import { BoardApprovalAllowedActionsResultDto } from '../_models/board-approval-allowed-actions-result-dto';
import { BoardApprovalQueueResultDto } from '../_models/board-approval-queue-result-dto';

@Injectable({
    providedIn: 'root'
})
export class BoardApprovalService {
    private readonly _subjectUpdateBoardApproval: Subject<boolean> = new Subject<boolean>();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getBoardApprovalQueue(baseParams: BoardApprovalQueueParameterDto): Observable<BoardApprovalQueueResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/queue${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(BoardApprovalQueueResultDto, response)));
    }

    getActions(baseParams: BoardApprovalAllowedActionsParameterDto): Observable<BoardApprovalAllowedActionsResultDto> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/actions${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(BoardApprovalAllowedActionsResultDto, response)));
    }

    approveMultipleBoardApprovalAcc(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/multi-select/action/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }

    denyMultipleBoardApprovalAcc(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/multi-select/action/deny`;
        return this.httpClient.post(url, classToPlain(params));
    }

    holdMultipleBoardApprovalAcc(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/multi-select/action/hold`;
        return this.httpClient.post(url, classToPlain(params));
    }

    approveMultipleWithStipulationBoardApprovalAcc(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/acc/multi-select/action/approve-with-stipulation`;
        return this.httpClient.post(url, classToPlain(params));
    }

    /* services for boardapproval/acc ends here */

    /* services for boardapproval/collections starts here */

    approveMultipleBoardApprovalCollection(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/multi-select/action/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }

    denyMultipleBoardApprovalCollection(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/multi-select/action/deny`;
        return this.httpClient.post(url, classToPlain(params));
    }

    holdMultipleBoardApprovalCollection(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/multi-select/action/hold`;
        return this.httpClient.post(url, classToPlain(params));
    }

    approveMultipleWithStipulationBoardApprovalCollection(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/collections/multi-select/action/approve-with-stipulation`;
        return this.httpClient.post(url, classToPlain(params));
    }

    /* services for boardapproval/collections ends here */

    /* services for boardapproval/wf starts here */

    approveMultipleBoardApprovalWf(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/multi-select/action/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }

    denyMultipleBoardApprovalWf(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/multi-select/action/deny`;
        return this.httpClient.post(url, classToPlain(params));
    }

    holdMultipleBoardApprovalWf(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/multi-select/action/hold`;
        return this.httpClient.post(url, classToPlain(params));
    }

    approveMultipleWithStipulationBoardApprovalWf(params: BoardApprovalsActionParametersDto): Observable<any> {
        const url = `${URLConstants.ccBoardApprovalConfigApiUrl}/wf/multi-select/action/approve-with-stipulation`;
        return this.httpClient.post(url, classToPlain(params));
    }

    /* services for boardapproval/wf ends here */

    /* Others */

    updateBoardApprovalRecords(): void {
        this._subjectUpdateBoardApproval.next(true);
    }

    refreshBoardApprovalRecords(): Observable<boolean> {
        return this._subjectUpdateBoardApproval;
    }
}
